import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import cogoToast from "cogo-toast";
import CustomBadge from "../../../../components/CustomBadge";
import { getLeagueAction } from "../../../../redux/actions/leagueAction";
import "../Wrapper/wrapper.css";
import "./leaderlist.css";
import { TextField, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import httpRequest from "../../../../ajax";

const tierStyles = {
  bronze:   { bg: "#f5e6d3", color: "#8b4513", label: "BRONZE",   icon: "🥉" },
  silver:   { bg: "#e8e8e8", color: "#555",    label: "SILVER",   icon: "🥈" },
  gold:     { bg: "#fef3c7", color: "#a16207", label: "GOLD",     icon: "🥇" },
  platinum: { bg: "#e0f2fe", color: "#0369a1", label: "PLATINUM", icon: "💎" },
};

const getTierFromPoints = (points) => {
  if (points >= 200) return "platinum";
  if (points >= 60)  return "gold";
  if (points >= 20)  return "silver";
  return "bronze";
};

const LeaderList = () => {
  const dispatch = useDispatch();
  const { league, userOptedIn } = useSelector(({ league }) => league);
  const [searchTerm, setSearchTerm] = useState("");
  const [optingIn, setOptingIn] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    getLeagueAction(dispatch);
  }, [dispatch]);

  const handleOptIn = async (optIn = true) => {
    setOptingIn(true);
    try {
      const res = await httpRequest("put", "/leaderboard/opt-in", { opt_in: optIn });
      if (res?.data?.status) {
        if (optIn) setDismissed(true);
        // Invalidate React Query cache so sidebar updates too
        queryClient.invalidateQueries('wbLeague');
        // Refresh league data so names/avatars update
        getLeagueAction(dispatch);
      }
    } catch (err) {
      console.error("Opt-in failed:", err);
    } finally {
      setOptingIn(false);
    }
  };

  if (!league || !Array.isArray(league)) {
    return (
      <div className="container mt-4">
        <p className="text-center">Complete your profile to view League List</p>
      </div>
    );
  }

  const filteredLeague = league.filter((person) => {
    const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
    const username = person.username.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || username.includes(search);
  });

  return (
    <div className="league-container">
      {/* ── Consent Banner ── */}
      {!userOptedIn && !dismissed && (
        <div className="consent-banner">
          <div className="consent-banner-content">
            <span className="consent-banner-icon">🔒</span>
            <div className="consent-banner-text">
              <strong>Your name and photo are hidden on the leaderboard.</strong>
              <br />
              <span>Show your identity to let others see who you are on the WB League.</span>
            </div>
            <div className="consent-banner-actions">
              <button
                className="consent-btn-opt-in"
                onClick={() => handleOptIn(true)}
                disabled={optingIn}
              >
                {optingIn ? "Updating..." : "Show my identity"}
              </button>
              <button
                className="consent-btn-dismiss"
                onClick={() => {
                  setDismissed(true);
                  cogoToast.info(
                    "You can change your leaderboard visibility anytime from your Profile settings.",
                    { position: "top-center", heading: "Visibility Reminder", hideAfter: 5 }
                  );
                }}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Opted-in confirmation ── */}
      {userOptedIn && (
        <div className="consent-banner consent-banner-confirmed">
          <div className="consent-banner-content">
            <span className="consent-banner-icon">✅</span>
            <div className="consent-banner-text">
              <strong>Your name and photo are visible on the leaderboard.</strong>
            </div>
            <div className="consent-banner-actions">
              <button
                className="consent-btn-dismiss"
                onClick={() => handleOptIn(false)}
                disabled={optingIn}
              >
                {optingIn ? "Updating..." : "Hide my identity"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="search-container mb-4">
        <div className="search-wrapper">
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by name or username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="search-icon" />
                </InputAdornment>
              ),
              className: "search-input",
            }}
          />
          {searchTerm && (
            <button className="search-clear-btn" onClick={() => setSearchTerm("")}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="league-table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>User Info</th>
              <th>Tier</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeague.map((person, index) => {
              const points = person.points ?? ((person.inviteCount ?? 0) * 1 + (person.placementCount ?? 0) * 20);
              const tier = person.tier ?? getTierFromPoints(points);
              const ts = tierStyles[tier] || tierStyles.bronze;
              const isAnon = person.is_anonymized === 1 || person.is_anonymized === true;
              const displayName = isAnon
                ? (person.display_name || "Referrer_0000")
                : `${person.first_name} ${person.last_name}`;
              return (
                <tr key={person.id || index} className={index % 2 === 0 ? "even" : "odd"}>
                  <td>
                    <div className="rank-number">{index + 1}</div>
                  </td>
                  <td>
                    <div className="user-info">
                      {isAnon ? (
                        <div className="avatar-img" style={{
                          width: "40px", height: "40px", borderRadius: "50%",
                          backgroundColor: "#e2e8f0", flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="#94a3b8">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        </div>
                      ) : (
                        <img
                          className="avatar-img"
                          src={person.image_url}
                          alt="avatar"
                        />
                      )}
                      <div className="user-details">
                        <h6 className="user-name">
                          {displayName}
                        </h6>
                        {!isAnon && (
                          <span className="user-handle">@{person.username}</span>
                        )}
                        <span className="user-breakdown" style={{ fontSize: "11px", color: "#94a3b8" }}>
                          {person.inviteCount ?? 0} invite{((person.inviteCount ?? 0) === 1) ? "" : "s"} · {person.placementCount ?? 0} placement{((person.placementCount ?? 0) === 1) ? "" : "s"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: "4px",
                        backgroundColor: ts.bg,
                        color: ts.color,
                        letterSpacing: "0.3px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "3px",
                      }}
                    >
                      <span style={{ fontSize: "11px" }}>{ts.icon}</span>
                      {ts.label}
                    </span>
                  </td>
                  <td>
                    <div className="position-badge">
                      <CustomBadge figure={String(points)} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredLeague.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  No results found for &ldquo;{searchTerm}&rdquo;
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaderList;
